import { PlusCircleOutlined, CloseSquareOutlined, PlusOutlined, SaveOutlined } from '@ant-design/icons';
import { Button, Card, Checkbox, Col, Form, Input, InputNumber, message, Modal, Row, Space, Timeline } from 'antd';
import React, { useState } from 'react';
import ZTable, { IFilterItem, ITableRef, IColumnType } from '@/components/ZTable';
import { useTranslation } from 'react-i18next';
import { ModalWidth, ROW_GUTTER, UNKNOWN_ERROR_MESSAGE, VALIDATE_REQUIRED } from '@/config';
import BooleanTag from '@/components/app/BooleanTag';
import FormItem from 'antd/es/form/FormItem';
import { uniqBy } from 'lodash';
import TicketConditionInput from '@/components/FormItems/TicketConditionInput';
import { TicketCancellationPolicySettingDto } from '@/__generated';
import { apiSdk } from '@/httpClient';

const PRE_LINE = '------------------------------';
const columns: IColumnType<TicketCancellationPolicySettingDto>[] = [
  {
    title: 'ticketModificationSetting_TicketCondition',
    dataIndex: 'ticketConditionName',
  },
  {
    title: 'cancellationPolicyPage_IsPercentageBase',
    dataIndex: 'isPercentageBase',
    sorter: true,
    render: v => <BooleanTag value={v} />,
    align: 'center',
    width: 200,
  },
];

const filters: Array<IFilterItem> = [
  { type: 'SELECT', name: 'ticketConditionId', label: 'ticketModificationSetting_TicketCondition', customElm: <TicketConditionInput /> },
];

const CancellationPolicyTab = () => {
  const [t] = useTranslation();
  const zTb = React.useRef<ITableRef>();
  const [form] = Form.useForm();
  const [formVisible, setFormVisible] = useState(false);
  const [isNewForm, setIsNewForm] = useState(false);
  const [loading, setLoading] = useState(false);

  const createData = values => {
    setLoading(true);
    apiSdk.TicketCancellationPolicySettingModelService.create({ body: values })
      .then(response => {
        if (response?.success) {
          setFormVisible(false);
          zTb.current?.reload();
        } else {
          if (response.message) {
            message.error(t(response.message || UNKNOWN_ERROR_MESSAGE));
          }
        }
      })
      .catch(error => {
        message.error(t(UNKNOWN_ERROR_MESSAGE));
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const updateData = values => {
    setLoading(true);
    apiSdk.TicketCancellationPolicySettingModelService.update({ body: values })
      .then(response => {
        if (response?.success) {
          setFormVisible(false);
          zTb.current?.reload();
        } else {
          if (response.message) {
            message.error(t(response.message || UNKNOWN_ERROR_MESSAGE));
          }
        }
      })
      .catch(error => {
        message.error(t(UNKNOWN_ERROR_MESSAGE));
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const hasDuplicateHours = (plans: Array<{ hours: number }>) => {
    plans = plans.filter(x => x && x.hours !== undefined && x.hours !== null);
    let hasDuplicate = uniqBy(plans, x => x.hours).length !== plans.length;
    return hasDuplicate;
  };

  const checkDataBeforeSubmit = (values: any) => {
    if (hasDuplicateHours(values.plans)) {
      Modal.error({
        title: t('Error'),
        content: t('cancellationPolicyPage_DuplicateDaysError'),
      });
      return false;
    }
    return true;
  };
  const isPercentageBase = Form.useWatch(['isPercentageBase'], form);
  return (
    <div>
      <ZTable
        columns={columns}
        primaryKey="id"
        tableRef={zTb}
        url="/bookings/ticket-cancellation-policy-settings/get-ticket-cancellation-policy-settings"
        bordered
        order_by="createdOn"
        order_by_asc={false}
        allowEdit={true}
        onEdit={record => {
          setIsNewForm(false);
          setFormVisible(true);
          form.setFieldsValue({
            ...record,
          });
        }}
        allowDelete={true}
        delete_url="/bookings/ticket-cancellation-policy-settings/delete"
        filters={filters}
        searchBtnAlignLeft
        extraBtns={[
          <Button
            key="create"
            type="primary"
            className="nta-create-btn"
            icon={<PlusCircleOutlined />}
            onClick={() => {
              form.resetFields();
              setIsNewForm(true);
              setFormVisible(true);
            }}
          >
            {t('createNew')}
          </Button>,
        ]}
      ></ZTable>
      <Modal
        open={formVisible}
        maskClosable={false}
        width={ModalWidth.medium}
        className="nta-custom-form"
        title={isNewForm ? t('cancellationPolicyPage_Create') : t('cancellationPolicyPage_Update')}
        forceRender={true}
        okText={t('save')}
        cancelText={t('cancel')}
        okButtonProps={{ icon: <SaveOutlined />, className: 'save-btn', loading: loading }}
        onCancel={() => {
          form.resetFields();
          setFormVisible(false);
        }}
        onOk={() => {
          form
            .validateFields()
            .then(values => {
              if (checkDataBeforeSubmit(values)) {
                if (values.id) {
                  updateData(values);
                } else {
                  createData(values);
                }
              }
            })
            .catch(info => {});
        }}
      >
        <Form
          form={form}
          size="middle"
          layout="vertical"
          initialValues={{
            isPercentageBase: true,
            plans: [{ days: 0, amount: 100 }],
          }}
        >
          <Form.Item name="id" className="hidden">
            <Input type="hidden" />
          </Form.Item>
          <Row gutter={ROW_GUTTER}>
            <Col md={12}>
              <Form.Item
                label={t('cancellationPolicyPage_TicketCondition')}
                name="ticketConditionId"
                rules={[{ required: true, message: VALIDATE_REQUIRED }]}
              >
                <TicketConditionInput placeholder={t('cancellationPolicyPage_TicketCondition')} />
              </Form.Item>
            </Col>
            <Col md={12}>
              <Form.Item valuePropName="checked" label={'  '} name="isPercentageBase">
                <Checkbox>{t('cancellationPolicyPage_IsPercentageBase')}</Checkbox>
              </Form.Item>
            </Col>
            <Col md={24}>
              <Form.List name="plans">
                {(fields, { add, remove }) => {
                  return (
                    <Card
                      size="small"
                      title={<b>{t('cancellationPolicyPage_Plans')}</b>}
                      extra={<Button type="primary" onClick={() => add()} icon={<PlusOutlined />}></Button>}
                    >
                      <Timeline mode="left" style={{ marginTop: 20 }}>
                        <Timeline.Item label={<b>{t('cancellationPolicyPage_Hours')}</b>}>
                          <b>{t('cancellationPolicyPage_Amount')}</b>
                        </Timeline.Item>
                        <Timeline.Item label={PRE_LINE}>{PRE_LINE}</Timeline.Item>
                        {fields.map((field, index) => {
                          let hours = parseInt(form.getFieldValue(['plans', field.name, 'hours']) || '0');
                          let isCanNotDelete = hours === 0 && index === 0;
                          let label =
                            hours === 0 && index === 0 ? (
                              'Check in time'
                            ) : (
                              <Space>
                                <Form.Item {...field} name={[field.name, 'hours']} rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                                  <InputNumber min={1} addonAfter={t('hours')} addonBefore={t('Before')} style={{ width: 200 }} />
                                </Form.Item>
                              </Space>
                            );
                          return (
                            <Timeline.Item label={label} color={index === 0 ? 'red' : index === 1 ? '#1890ff' : 'gray'}>
                              <div style={{ display: 'flex' }}>
                                <Form.Item {...field} name={[field.name, 'amount']} rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                                  <InputNumber style={{ width: 170 }} min={0} addonAfter={isPercentageBase ? '%' : undefined} />
                                </Form.Item>

                                {isCanNotDelete ? null : (
                                  <Button type="link" danger icon={<CloseSquareOutlined onClick={() => remove(field.name)} />}></Button>
                                )}
                              </div>
                            </Timeline.Item>
                          );
                        })}
                      </Timeline>
                    </Card>
                  );
                }}
              </Form.List>
              <FormItem dependencies={['plans']} shouldUpdate noStyle>
                {({ getFieldValue }) => {
                  let plans: Array<{ hours: number }> = getFieldValue(['plans']) || [];
                  let hasDuplicate = hasDuplicateHours(plans);
                  return hasDuplicate ? <div className="error-message">{t('cancellationPolicyPage_DuplicateHoursError')}</div> : null;
                }}
              </FormItem>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
};
export default CancellationPolicyTab;
